import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.4)"
      : "rgba(0, 0, 0, 0.6)"};
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: ${fadeIn} 0.2s ease;
`;

export const ModalContainer = styled.div`
  width: 90%;
  max-width: 480px;
  font-size: 1em;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 252, 0.98) 100%)"
      : "linear-gradient(135deg, rgb(20, 20, 25) 0%, rgb(15, 15, 20) 100%)"};
  backdrop-filter: blur(20px);
  border-radius: 14px;
  border: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.1)"
        : "rgba(255, 255, 255, 0.08)"};
  box-shadow: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05) inset"
      : "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05) inset"};
  overflow: hidden;
  animation: ${slideUp} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.2em 1.4em;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.08)"
        : "rgba(255, 255, 255, 0.06)"};
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(255, 255, 255, 0.5)"
      : "rgba(255, 255, 255, 0.02)"};
`;

export const ModalTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-size: 1em;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  margin: 0;

  svg {
    font-size: 1em;
    color: ${({ theme }) => theme.primary};
  }
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.8em;
  height: 1.8em;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.05)"
      : "rgba(255, 255, 255, 0.06)"};
  border: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.08)"
        : "rgba(255, 255, 255, 0.08)"};
  border-radius: 7px;
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 1.1em;
  }

  &:hover {
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.08)"
        : "rgba(255, 255, 255, 0.1)"};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const ModalBody = styled.div`
  padding: 1.2em 1.4em;
  max-height: 60vh;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.03)"
        : "rgba(255, 255, 255, 0.03)"};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.15)"
        : "rgba(255, 255, 255, 0.15)"};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) =>
        theme.body === "#fafbfc"
          ? "rgba(0, 0, 0, 0.25)"
          : "rgba(255, 255, 255, 0.25)"};
    }
  }
`;

export const FilterSection = styled.div`
  margin-bottom: 1.4em;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.4em;
  font-size: 0.85em;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  margin-bottom: 0.8em;
  opacity: 0.85;

  svg {
    font-size: 0.95em;
    color: ${({ theme }) => theme.primary};
  }
`;

export const SectionActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.8em;

  ${SectionTitle} {
    margin-bottom: 0;
  }
`;

export const SelectAllButton = styled.button`
  padding: 0.4em 0.8em;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.04)"
      : "rgba(255, 255, 255, 0.05)"};
  border: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.1)"
        : "rgba(255, 255, 255, 0.1)"};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};
  font-size: 0.75em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.06)"
        : "rgba(255, 255, 255, 0.08)"};
  }

  &:active {
    transform: scale(0.97);
  }
`;

export const DateInputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8em;

  .input-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.4em;

    label {
      font-size: 0.7em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  }

  .separator {
    font-size: 0.8em;
    font-weight: 500;
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    margin-top: 1.2em;
  }
`;

export const DateInput = styled.input`
  padding: 0.6em 0.8em;
  background: ${({ theme, hasError }) =>
    hasError
      ? theme.body === "#fafbfc"
        ? "rgba(239, 68, 68, 0.05)"
        : "rgba(239, 68, 68, 0.08)"
      : theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.04)"
      : "rgba(255, 255, 255, 0.05)"};
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError
        ? "#ef4444"
        : theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.1)"
        : "rgba(255, 255, 255, 0.1)"};
  border-radius: 7px;
  color: ${({ theme }) => theme.text};
  font-size: 0.85em;
  font-weight: 500;
  transition: all 0.2s ease;

  &::placeholder {
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme, hasError }) =>
      hasError ? "#ef4444" : theme.primary};
    background: ${({ theme, hasError }) =>
      hasError
        ? theme.body === "#fafbfc"
          ? "rgba(239, 68, 68, 0.08)"
          : "rgba(239, 68, 68, 0.12)"
        : theme.body === "#fafbfc"
        ? "rgba(16, 185, 129, 0.05)"
        : "rgba(16, 185, 129, 0.08)"};
    box-shadow: 0 0 0 3px
      ${({ theme, hasError }) =>
        hasError
          ? "rgba(239, 68, 68, 0.15)"
          : theme.body === "#fafbfc"
          ? "rgba(16, 185, 129, 0.1)"
          : "rgba(16, 185, 129, 0.15)"};
  }
`;

export const DateError = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4em;
  margin-top: 0.4em;
  padding: 0.4em 0.6em;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(239, 68, 68, 0.08)"
      : "rgba(239, 68, 68, 0.12)"};
  border-radius: 5px;
  border: 1px solid rgba(239, 68, 68, 0.2);

  svg {
    font-size: 0.85em;
    color: #ef4444;
    flex-shrink: 0;
  }

  span {
    font-size: 0.75em;
    font-weight: 500;
    color: ${({ theme }) =>
      theme.body === "#fafbfc" ? "#dc2626" : "#f87171"};
    line-height: 1.3;
  }
`;

export const CompanyInputGroup = styled.div`
  position: relative;
  margin-bottom: 0.8em;

  .search-icon {
    position: absolute;
    left: 0.8em;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.9em;
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
    pointer-events: none;
  }
`;

export const CompanyInput = styled.input`
  width: 100%;
  padding: 0.6em 0.8em 0.6em 2.4em;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.04)"
      : "rgba(255, 255, 255, 0.05)"};
  border: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.1)"
        : "rgba(255, 255, 255, 0.1)"};
  border-radius: 7px;
  color: ${({ theme }) => theme.text};
  font-size: 0.85em;
  font-weight: 500;
  transition: all 0.2s ease;

  &::placeholder {
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(16, 185, 129, 0.05)"
        : "rgba(16, 185, 129, 0.08)"};
    box-shadow: 0 0 0 3px
      ${({ theme }) =>
        theme.body === "#fafbfc"
          ? "rgba(16, 185, 129, 0.1)"
          : "rgba(16, 185, 129, 0.15)"};
  }
`;

export const CompanyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4em;
  max-height: 240px;
  overflow-y: auto;
  padding: 0.3em;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.02)"
      : "rgba(255, 255, 255, 0.02)"};
  border-radius: 7px;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.15)"
        : "rgba(255, 255, 255, 0.15)"};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) =>
        theme.body === "#fafbfc"
          ? "rgba(0, 0, 0, 0.25)"
          : "rgba(255, 255, 255, 0.25)"};
    }
  }
`;

export const CompanyIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2em;
  height: 2em;
  min-width: 2em;
  background: ${({ color }) => color || "#10b981"};
  border-radius: 6px;
  box-shadow: 0 2px 6px ${({ color }) => color || "#10b981"}30;
  transition: box-shadow 0.15s ease;

  svg {
    font-size: 1em;
    color: white;
  }
`;

export const CompanyCheckbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.3em;
  height: 1.3em;
  min-width: 1.3em;
  background: ${({ theme, isSelected }) =>
    isSelected
      ? theme.primary
      : theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.04)"
      : "rgba(255, 255, 255, 0.06)"};
  border: 1.5px solid
    ${({ theme, isSelected }) =>
      isSelected
        ? theme.primary
        : theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.15)"
        : "rgba(255, 255, 255, 0.15)"};
  border-radius: 4px;
  transition: all 0.15s ease;

  svg {
    font-size: 0.75em;
    color: white;
    stroke-width: 3px;
  }
`;

export const CompanyItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7em;
  padding: 0.6em 0.8em;
  background: ${({ theme, isSelected }) =>
    isSelected
      ? theme.body === "#fafbfc"
        ? "rgba(16, 185, 129, 0.06)"
        : "rgba(16, 185, 129, 0.08)"
      : theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.02)"
      : "rgba(255, 255, 255, 0.03)"};
  border: 1px solid
    ${({ theme, isSelected }) =>
      isSelected
        ? "rgba(16, 185, 129, 0.2)"
        : theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.05)"
        : "rgba(255, 255, 255, 0.05)"};
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.15s ease;

  .company-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.2em;
    min-width: 0;

    .company-name {
      font-size: 0.85em;
      font-weight: ${({ isSelected }) => (isSelected ? 600 : 500)};
      color: ${({ theme }) => theme.text};
      opacity: ${({ isSelected }) => (isSelected ? 1 : 0.9)};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .company-cnpj {
      font-size: 0.7em;
      font-weight: 400;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      font-family: 'Courier New', monospace;
    }
  }

  &:hover {
    background: ${({ theme, isSelected }) =>
      isSelected
        ? theme.body === "#fafbfc"
          ? "rgba(16, 185, 129, 0.1)"
          : "rgba(16, 185, 129, 0.12)"
        : theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.04)"
        : "rgba(255, 255, 255, 0.05)"};

    ${CompanyIcon} {
      box-shadow: 0 3px 10px ${({ color }) => color || "#10b981"}40;
    }
  }

  &:active {
    transform: scale(0.99);
  }
`;

export const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.7em;
  padding: 1.2em 1.4em;
  border-top: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.08)"
        : "rgba(255, 255, 255, 0.06)"};
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(255, 255, 255, 0.5)"
      : "rgba(255, 255, 255, 0.02)"};
`;

export const ClearButton = styled.button`
  padding: 0.6em 1.3em;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.05)"
      : "rgba(255, 255, 255, 0.06)"};
  border: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.1)"
        : "rgba(255, 255, 255, 0.1)"};
  border-radius: 7px;
  color: ${({ theme }) => theme.text};
  font-size: 0.82em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.08)"
        : "rgba(255, 255, 255, 0.1)"};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const ApplyButton = styled.button`
  padding: 0.6em 1.5em;
  background: ${({ theme }) => theme.primary};
  border: none;
  border-radius: 7px;
  color: white;
  font-size: 0.82em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35);
  }

  &:active {
    transform: translateY(0);
  }
`;
